/**
 * The verticals a utility button can target. Only the three the feed can create into are named — the
 * endpoint returns many more, and anything unlisted simply never matches.
 */
export enum FunctionalVertical {
  RESOURCE_BOOKING = 'RESOURCE_BOOKING',
  VISITOR_REGISTRATION = 'VISITOR_REGISTRATION',
  SERVICE_REQUEST = 'SERVICE_REQUEST',
}

/**
 * How a utility button opens. Only MICROFRONTEND and NATIVE open HqO's own experience for the vertical;
 * the rest link out (an external site in a webview, a host deeplink, a native link), so the feed's
 * in-app create flow cannot stand in for them.
 */
export enum UtilityButtonType {
  WEBVIEW = 'WEBVIEW',
  NATIVE = 'NATIVE',
  MICROFRONTEND = 'MICROFRONTEND',
  NATIVELINK = 'NATIVELINK',
  DEEPLINK = 'DEEPLINK',
}

/**
 * A building's utility button, as the tenant endpoint serves it.
 *
 * The endpoint has already done the per-user work — disabled buttons, feature-access denials, per-user
 * permissions and platform visibility are all filtered server-side — so a button coming back means this
 * user, in this building, can actually use that vertical.
 */
export interface UtilityButton {
  uuid: string;
  /** How the button opens; see `UtilityButtonType`. Always sent by the endpoint. */
  type?: UtilityButtonType | string | null;
  /** Where the button goes. Null for a NATIVE button, which opens a host screen rather than a url. */
  url?: string | null;
  /**
   * The app instance config behind the button: its own, or the building's config for the vertical when
   * the button names none. Filled in by `include=VERTICAL_ADAPTER`, together with `vertical_adapter`.
   */
  app_instance_uuid?: string | null;
  /**
   * The integration that config runs on, by bare adapter name ('VisitorRegistrationBuiltinAdapter',
   * 'CloudgateAdapter'). Derived per request by `include=VERTICAL_ADAPTER`; absent when the building has
   * no config for the vertical.
   */
  vertical_adapter?: string | null;
  /** Absent on some buttons; `feature_type` then carries the vertical instead. */
  vertical?: FunctionalVertical | string | null;
  /** Set from the building's app config. `SERVICE_REQUESTS_V2` has no `vertical` of its own. */
  feature_type?: string | null;
  /** Lucide component name ('UserRoundPen'), authored per building in company-admin. */
  lucide_icon?: string | null;
  /** Display order within a vertical. The lowest wins when a building has several. */
  order: number;
}

export interface GetUtilityButtonsAPIResponse {
  data: { utility_buttons: UtilityButton[] };
}
